import React from 'react';
import './SupportSecurityPage.css';

export default function SupportSecurityPage() {
  const certificateStatus = 'ready';

  const securitySteps = [
    {
      phase: 'Phase 1',
      title: 'Documents at Rest',
      status: 'complete',
      description: 'All user documents encrypted with AES-256-GCM',
      details: [
        'Encryption: 256-bit symmetric key',
        'Authentication: 128-bit tags detect tampering',
        'Storage: Persistent Docker volume with IV prepended',
        'Transparent decryption on download'
      ]
    },
    {
      phase: 'Phase 2',
      title: 'Database Security',
      status: 'complete',
      description: 'Database credentials and encryption configured',
      details: [
        'SSL/TLS support for database connections',
        'Environment variables for secrets management',
        'Field-level encryption for sensitive data',
        'Password hashing with BCrypt'
      ]
    },
    {
      phase: 'Phase 3',
      title: 'HTTPS Certificate',
      status: 'complete',
      description: 'SSL/TLS certificate active for via-ventus.nl and www.via-ventus.nl',
      details: [
        'AWS ACM certificate issued and validated',
        'Certificate covers via-ventus.nl and www.via-ventus.nl',
        'HTTPS listener (443) active on the load balancer',
        'HTTP traffic automatically redirects to HTTPS'
      ]
    },
    {
      phase: 'Phase 4',
      title: 'DNS & Load Balancing',
      status: 'complete',
      description: 'Route 53 A records, ALB configuration, and network isolation',
      details: [
        'A Alias records active for via-ventus.nl and www',
        'Traffic routed through Application Load Balancer',
        'Load balancer runs in its own security zone (dedicated security group boundary)',
        'Application security group only accepts ingress from the load balancer security group',
        'Health checks ensure target availability'
      ]
    },
    {
      phase: 'Phase 5',
      title: 'Secret Management',
      status: 'pending',
      description: 'Production-ready secrets storage',
      details: [
        'Migrate to AWS Secrets Manager',
        'Rotate encryption keys from repository',
        'Enable database volume encryption',
        'Setup audit logging for all access'
      ]
    }
  ];

  return (
    <div className="v-page support-security-page">
      <div className="v-page__header support-header">
        <div>
          <h1 className="v-page__title">Security & Support</h1>
          <p className="v-page__subtitle">Infrastructure encryption and HTTPS deployment status</p>
        </div>
      </div>

      <div className="security-container">
        <div className="security-intro">
          <div className="intro-card">
            <h2>Security Flow Overview</h2>
            <p>
              Your Ventus platform is running with enterprise-grade security controls. Below is the 
              current implementation status across encryption, networking, and infrastructure layers.
            </p>
          </div>
        </div>

        <div className="security-timeline">
          {securitySteps.map((step, index) => (
            <div key={index} className={`security-step security-step--${step.status}`}>
              <div className="step-indicator">
                <div className={`step-badge step-badge--${step.status}`}>
                  {step.status === 'complete' && '✓'}
                  {step.status === 'ongoing' && '→'}
                  {step.status === 'pending' && '○'}
                </div>
              </div>

              <div className="step-content">
                <div className="step-header">
                  <span className="step-phase">{step.phase}</span>
                  <h3 className="step-title">{step.title}</h3>
                  <span className={`step-status step-status--${step.status}`}>
                    {step.status === 'complete' && 'Complete'}
                    {step.status === 'ongoing' && 'In Progress'}
                    {step.status === 'pending' && 'Pending'}
                  </span>
                </div>

                <p className="step-description">{step.description}</p>

                <div className="step-details">
                  {step.details.map((detail, idx) => (
                    <div key={idx} className="detail-item">
                      <span className="detail-bullet">–</span>
                      <span className="detail-text">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              {index < securitySteps.length - 1 && (
                <div className={`step-connector step-connector--${securitySteps[index + 1]?.status}`} />
              )}
            </div>
          ))}
        </div>

        <div className="security-notes">
          <h2>What This Means</h2>
          <div className="notes-grid">
            <div className="note-card">
              <h4>Documents</h4>
              <p>All uploaded files are encrypted with AES-256-GCM before storage. Only authorized workspace members can decrypt and access documents.</p>
            </div>
            <div className="note-card">
              <h4>Database</h4>
              <p>Database connections support SSL/TLS encryption. Credentials are managed through environment variables, enabling integration with secret managers.</p>
            </div>
            <div className="note-card">
              <h4>HTTPS</h4>
              <p>Your domain via-ventus.nl is configured for HTTPS with an AWS-issued SSL certificate. All traffic is encrypted end-to-end.</p>
            </div>
            <div className="note-card">
              <h4>Infrastructure</h4>
              <p>The load balancer sits in its own protected security zone with dedicated security groups, and backend services only accept traffic forwarded by the load balancer. Route 53 DNS ensures highly available routing over encrypted HTTPS connections.</p>
            </div>
          </div>
        </div>

        {certificateStatus === 'ready' && (
          <div className="certificate-success">
            <h2>HTTPS Active</h2>
            <div className="success-content">
              <p>Your SSL/TLS certificate is issued, validated, and in active use.</p>
              <p className="status-text">Status: <strong>Certificate Validated, HTTPS Enforced</strong></p>
              <p className="next-steps">
                HTTPS listener is active on the load balancer. Traffic to via-ventus.nl and 
                www.via-ventus.nl is encrypted and redirected from HTTP to HTTPS automatically.
              </p>
              <p className="next-steps">
                The load balancer operates inside a dedicated security zone, and only load-balancer-originated
                traffic is allowed to reach the application tier.
              </p>
            </div>
          </div>
        )}

        <div className="security-footer">
          <h2>Need Help?</h2>
          <p>
            For questions about your security configuration or to report a vulnerability, 
            please contact <strong>security@via-ventus.nl</strong>
          </p>
          <p className="footer-docs">
            See <strong>ENCRYPTION.md</strong> in your repository for detailed production security checklist and setup instructions.
          </p>
        </div>
      </div>
    </div>
  );
}
